import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/mysql/client';
import { useToast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api';
import { showApiError } from '@/lib/errors';
import type { UpdateContactData } from '@/lib/api/services/contacts';
import { getLimitErrorMessage, isLimitExceededError } from '@/hooks/useFeatureLimits';
import { coerceCustomFields } from '@/lib/constants/customFieldKeys';

// Backend response structure
export interface BackendContact {
  id: string
  businessAccountId: string
  name: string | null
  phoneNumber: string
  email: string
  tags: string[]
  customFields: Record<string, any>
  status: string
  notes: string
  campaignOptedOut?: boolean
  createdAt: string
  updatedAt: string
}

// Backend request structure for bulk import
export interface BulkImportContact {
  name: string
  phoneNumber: string
  email?: string
  tags?: string[]
  customFields?: Record<string, any>
  status?: string
  notes?: string
}

// Frontend Contact interface (for internal use)
export interface Contact {
  id: string
  tenant_id: string
  business_account_id: string
  name: string | null
  phone_number: string
  profile_picture_url: string | null
  status: string
  type?: 'LEAD' | 'CUSTOMER'
  custom_fields: Record<string, any>
  // Tag objects with color for rendering
  tags: any[]
  last_seen: string | null
  campaign_opted_out?: boolean
  campaignOptedOut?: boolean
  created_at: string
  updated_at: string
  // Computed fields
  tag_assignments?: Array<{
    id: string
    tag: {
      id: string
      name: string
      color: string
    }
  }>
  conversation_count?: number
  last_conversation?: string
}

export interface ContactTag {
  id: string
  tenant_id: string
  name: string
  color: string
  description: string | null
  created_at: string
  contact_count?: number
}

// Data transformation functions
export function transformBackendContactToFrontend(backendContact: BackendContact): Contact {
  return {
    id: backendContact.id,
    tenant_id: '', // Not provided by backend
    business_account_id: backendContact.businessAccountId,
    name: backendContact.name,
    phone_number: backendContact.phoneNumber,
    profile_picture_url: null, // Not provided by backend
    status: backendContact.status.toLowerCase(),
    type: (backendContact as any).type,
    custom_fields: {
      // `customFields` can arrive as a JSON string (Meta Lead Ad contacts /
      // RDS Data API read path). Spreading a raw string here would enumerate
      // its characters into the object — coerce to a real object first.
      ...coerceCustomFields(backendContact.customFields),
      email: backendContact.email // Move email from root to custom_fields
    },
    tags: (backendContact as any).tags || [],
    last_seen: null, // Not provided by backend
    campaign_opted_out: backendContact.campaignOptedOut || false,
    campaignOptedOut: backendContact.campaignOptedOut || false,
    created_at: backendContact.createdAt,
    updated_at: backendContact.updatedAt
  }
}

export function transformFrontendContactToBackend(frontendContact: Partial<Contact>): Partial<BackendContact> {
  const { custom_fields, ...rest } = frontendContact
  const { email, ...otherCustomFields } = custom_fields || {}

  return {
    ...rest,
    businessAccountId: frontendContact.business_account_id,
    phoneNumber: frontendContact.phone_number,
    email: email || '',
    customFields: otherCustomFields || {},
    status: frontendContact.status?.toUpperCase() || 'ACTIVE',
    createdAt: frontendContact.created_at,
    updatedAt: frontendContact.updated_at
  }
}

export function useContacts(
  businessAccountId?: string,
  options: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: 'LEAD' | 'CUSTOMER';
    tags?: string[];
    orderBy?: string;
    ascending?: boolean;
    source?: string;
    has_email?: boolean;
    has_phone?: boolean;
    created_from?: string;
    created_to?: string;
  } = {}
) {
  const {
    page = 1,
    limit,
    search,
    status,
    type,
    tags,
    orderBy,
    ascending,
    source,
    has_email,
    has_phone,
    created_from,
    created_to,
  } = options;

  const hasLimit = typeof limit === 'number' && limit > 0;
  const effectiveLimit = hasLimit ? limit : undefined;
  const offset = hasLimit ? (page - 1) * (effectiveLimit as number) : undefined;

  return useQuery({
    queryKey: [
      'contacts',
      businessAccountId,
      hasLimit ? page : 'all',
      effectiveLimit ?? 'all',
      search,
      status,
      type,
      tags,
      orderBy,
      ascending,
      source,
      has_email,
      has_phone,
      created_from,
      created_to,
    ],
    queryFn: async () => {
      if (!businessAccountId) return { data: [], totalCount: 0, hasMore: false };

      // Use the API client to fetch contacts
      const { contactsApi } = await import('@/lib/api');
      const result = await contactsApi.getByBusinessAccount(businessAccountId, {
        limit: effectiveLimit,
        offset,
        search,
        status,
        type,
        tags,
        orderBy,
        ascending,
        source,
        has_email,
        has_phone,
        created_from,
        created_to,
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      // Transform backend contacts to frontend format
      const backendContacts = result.data || [];
      return {
        data: backendContacts.map((contact: BackendContact) =>
          transformBackendContactToFrontend(contact)
        ),
        totalCount: result.totalCount || 0, // total across business account
        filteredTotalCount: (result as any).filteredTotalCount ?? (result.count ?? backendContacts.length),
        hasMore: result.hasMore || false,
        currentPage: hasLimit ? page : 1,
        totalPages: hasLimit
          ? Math.max(
            1,
            Math.ceil(
              (((result as any).filteredTotalCount ?? result.count ?? backendContacts.length) as number) /
              (effectiveLimit as number)
            )
          )
          : 1,
      };
    },
    enabled: !!businessAccountId
  });
}

export function useContactCounts(businessAccountId?: string) {
  return useQuery({
    queryKey: ['contact-counts', businessAccountId],
    queryFn: async () => {
      if (!businessAccountId) {
        return { total: 0 };
      }

      const { contactsApi } = await import('@/lib/api');
      const response = await contactsApi.getByBusinessAccount(businessAccountId, {
        limit: 1,
        offset: 0,
      });

      const total =
        (response as any).totalCount ??
        (response as any).count ??
        (response as any).filteredTotalCount ??
        0;

      return { total };
    },
    enabled: !!businessAccountId,
    staleTime: 60 * 1000,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (contactData: {
      businessAccountId: string;
      phoneNumber: string;
      name: string;
      email?: string;
      tags?: string[];
      customFields?: Record<string, any>;
      type?: 'LEAD' | 'CUSTOMER';
      campaignOptedOut?: boolean;
    }) => {
      // Use the API client to create contact
      const { contactsApi } = await import('@/lib/api');
      const result = await contactsApi.create({
        businessAccountId: contactData.businessAccountId,
        phoneNumber: contactData.phoneNumber,
        name: contactData.name,
        email: contactData.email || '',
        tags: contactData.tags || [],
        customFields: contactData.customFields || {},
        type: contactData.type,
        campaignOptedOut: contactData.campaignOptedOut ?? false,
        status: 'ACTIVE'
      } as any);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Also invalidate tags to refresh tag counts
      queryClient.invalidateQueries({ queryKey: ['tags', variables.businessAccountId] });
      // Invalidate contact stats to update "New contacts today"
      queryClient.invalidateQueries({ queryKey: ['contact-stats', variables.businessAccountId] });
      toast({ variant: "success",
        title: "Contact added",
        description: "New contact has been successfully created.",
      });
    },
    onError: (error: any) => {
      const errorMessage = getLimitErrorMessage(error);
      toast({
        title: isLimitExceededError(error) ? "Limit Reached" : "Error adding contact",
        description: errorMessage,
        variant: "destructive",
        duration: isLimitExceededError(error) ? 8000 : 5000, // Show limit errors longer
      });
    }
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, updates }: {
      id: string;
      updates: UpdateContactData;
    }) => {
      // Use the API client to update contact
      const { contactsApi } = await import('@/lib/api');
      const result = await contactsApi.update(id, updates);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: (data, variables) => {
      // Keep cached contact detail in sync (prevents needing a hard refresh)
      queryClient.setQueriesData(
        { queryKey: ['contact', variables.id] },
        (old: any) => (old ? { ...old, ...data } : data)
      )

      // Keep contacts lists in sync when they are already loaded
      queryClient.setQueriesData(
        { queryKey: ['contacts'] },
        (old: any) => {
          if (!old) return old
          if (Array.isArray(old)) {
            return old.map((c: any) => (c?.id === data?.id ? { ...c, ...data } : c))
          }
          if (Array.isArray(old?.data)) {
            return {
              ...old,
              data: old.data.map((c: any) => (c?.id === data?.id ? { ...c, ...data } : c)),
            }
          }
          return old
        }
      )

      // Sync contact tags/fields in inbox conversations cache
      queryClient.setQueriesData(
        { queryKey: ['conversations'] },
        (old: any) => {
          if (!old) return old
          if (Array.isArray(old)) {
            return old.map((conv: any) => {
              if (conv?.contact?.id !== data?.id) return conv
              return {
                ...conv,
                contact: {
                  ...conv.contact,
                  ...data,
                  // Ensure tags are updated on the nested contact
                  tags: (data as any)?.tags ?? conv.contact.tags,
                },
              }
            })
          }
          if (Array.isArray(old?.data)) {
            return {
              ...old,
              data: old.data.map((conv: any) => {
                if (conv?.contact?.id !== data?.id) return conv
                return {
                  ...conv,
                  contact: {
                    ...conv.contact,
                    ...data,
                    tags: (data as any)?.tags ?? conv.contact.tags,
                  },
                }
              }),
            }
          }
          return old
        }
      )

      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Also invalidate tags to refresh tag counts when contact tags are updated
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      // Invalidate contact stats (in case unsubscribed status changed)
      queryClient.invalidateQueries({ queryKey: ['contact-stats'] });
      // Invalidate conversations so updated contact tags/fields reflect in inbox without refresh
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      // Invalidate kanban board views so lead/customer cards update tag badges without refresh
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey as any[]
          return Array.isArray(key) && key.length === 3 && key[0] === 'board' && key[2] === 'kanban'
        },
      })
      toast({ variant: "success",
        title: "Contact updated",
        description: "Contact information has been successfully updated.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error updating contact" } });
    }
  });
}

export function useDeactivateContact() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (contactId: string) => {
      const { contactsApi } = await import('@/lib/api')
      const result = await contactsApi.update(contactId, { status: 'INACTIVE' })

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    onSuccess: (_, contactId) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact-stats'] })

      toast({ variant: "success",
        title: 'Contact deactivated',
        description: 'The contact has been marked as inactive.',
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: 'Error deactivating contact' } })
    },
  })
}

export function useActivateContact() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (contactId: string) => {
      const { contactsApi } = await import('@/lib/api')
      const result = await contactsApi.update(contactId, { status: 'ACTIVE' })

      if (result.error) {
        throw ApiError.fromResult(result)
      }

      return result.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact-stats'] })

      toast({ variant: "success",
        title: 'Contact activated',
        description: 'The contact has been marked as active.',
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: 'Error activating contact' } })
    },
  })
}

export function useBulkDeactivateContacts() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (contactIds: string[]) => {
      if (!Array.isArray(contactIds) || contactIds.length === 0) {
        throw new Error('No contacts selected')
      }

      const { contactsApi } = await import('@/lib/api')

      await Promise.all(
        contactIds.map(async (contactId) => {
          const result = await contactsApi.update(contactId, { status: 'INACTIVE' })
          if (result.error) {
            throw ApiError.fromResult(result)
          }
        })
      )

      return contactIds
    },
    onSuccess: (_, contactIds) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact-stats'] })

      const count = contactIds.length
      toast({ variant: "success",
        title: 'Contacts deactivated',
        description: `${count} contact${count === 1 ? '' : 's'} marked as inactive.`,
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: 'Error deactivating contacts' } })
    },
  })
}

export function useBulkActivateContacts() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (contactIds: string[]) => {
      if (!Array.isArray(contactIds) || contactIds.length === 0) {
        throw new Error('No contacts selected')
      }

      const { contactsApi } = await import('@/lib/api')

      await Promise.all(
        contactIds.map(async (contactId) => {
          const result = await contactsApi.update(contactId, { status: 'ACTIVE' })
          if (result.error) {
            throw ApiError.fromResult(result)
          }
        })
      )

      return contactIds
    },
    onSuccess: (_, contactIds) => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact-stats'] })

      const count = contactIds.length
      toast({ variant: "success",
        title: 'Contacts activated',
        description: `${count} contact${count === 1 ? '' : 's'} marked as active.`,
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: 'Error activating contacts' } })
    },
  })
}

export function useDeleteContact() {
  return useDeactivateContact()
}

export function useBulkImportContacts() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      contacts,
      businessAccountId,
      fileUrl
    }: {
      contacts: Array<{
        name: string
        phoneNumber: string
        email?: string
        customFields?: Record<string, any>
        tags?: string[]
        status?: string
        notes?: string
      }>
      businessAccountId: string
      fileUrl?: string
    }) => {
      const { contactsApi } = await import('@/lib/api');
      const res = await contactsApi.bulkImport(businessAccountId, contacts, fileUrl)
      if ((res as any)?.error) {
        throw ApiError.fromResult(res as any)
      }
      return res
    },
    onSuccess: (response, variables) => {
      // Refresh contacts list (general + account-specific)
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      if (variables?.businessAccountId) {
        queryClient.invalidateQueries({ queryKey: ['contacts', variables.businessAccountId] })
        queryClient.invalidateQueries({ queryKey: ['tags', variables.businessAccountId] })
      } else {
        queryClient.invalidateQueries({ queryKey: ['tags'] })
      }

      const result: any =
        (response as any)?.data && typeof (response as any).data === 'object'
          ? (response as any).data
          : (response as any)

      if (result?.jobId) {
        toast({ variant: "success",
          title: "Import started",
          description: "Import processing in background. You will be notified when complete.",
        })
        return
      }

      const imported = Number(result?.imported || 0)
      const failed = Number(result?.failed || 0)
      const duplicates = Number(result?.duplicates || 0)
      const errorsCount = Array.isArray(result?.errors) ? result.errors.length : 0

      toast({ variant: "success",
        title: "Import completed",
        description: `Imported ${imported}, duplicates ${duplicates}, failed ${failed}${errorsCount ? ` (errors: ${errorsCount})` : ''}.`,
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Bulk import failed" } })
    }
  })
}

// Contact Tags Hooks
export function useContactTags() {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['contact-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contact_tags')
        .select('*')


      if (error) throw error

      // Transform the data to match our interface
      const transformedData = data?.map(tag => ({
        ...tag,
        contact_count: Array.isArray(tag.contact_count) ? tag.contact_count[0]?.count || 0 : 0
      })) || []

      return transformedData as ContactTag[]
    }
  })
}

export function useCreateContactTag() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (tagData: {
      name: string
      color?: string
      description?: string
    }) => {
      const { data, error } = await supabase
        .from('contact_tags')
        .insert({
          name: tagData.name,
          color: tagData.color || '#6B7280',
          description: tagData.description
        })
        .select()


      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact-tags'] })
      toast({ variant: "success",
        title: "Tag created",
        description: "Contact tag has been created successfully.",
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Error creating tag" } })
    }
  })
}

export function useAssignContactTags() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      contactId,
      tagIds
    }: {
      contactId: string
      tagIds: string[]
    }) => {
      // First, remove existing assignments
      await supabase
        .from('contact_tag_assignments')
        .delete()
        .eq('contact_id', contactId)

      // Then add new assignments
      if (tagIds.length > 0) {
        const assignments = tagIds.map(tagId => ({
          contact_id: contactId,
          tag_id: tagId
        }))

        const { error } = await supabase
          .from('contact_tag_assignments')
          .insert(assignments)

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      queryClient.invalidateQueries({ queryKey: ['contact-tags'] })
      toast({ variant: "success",
        title: "Tags updated",
        description: "Contact tags have been updated successfully.",
      })
    },
    onError: (error: any) => {
      showApiError(error, { fallback: { title: "Error updating tags" } })
    }
  })
}

export function useBlockContact() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, businessAccountId }: { id: string; businessAccountId: string }) => {
      const { contactsApi } = await import('@/lib/api');
      const result = await contactsApi.block(id, businessAccountId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Invalidate conversations to update contact status in the conversation list
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({ variant: "success",
        title: "Contact blocked",
        description: "Contact has been successfully blocked.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error blocking contact" } });
    }
  });
}

export function useUnblockContact() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, businessAccountId }: { id: string; businessAccountId: string }) => {
      const { contactsApi } = await import('@/lib/api');
      const result = await contactsApi.unblock(id, businessAccountId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      // Invalidate conversations to update contact status in the conversation list
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast({ variant: "success",
        title: "Contact unblocked",
        description: "Contact has been successfully unblocked.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error unblocking contact" } });
    }
  });
}

export function useBlockedContacts(businessAccountId?: string) {
  return useQuery({
    queryKey: ['contacts', 'blocked', businessAccountId],
    queryFn: async () => {
      if (!businessAccountId) return { data: [], count: 0, totalCount: 0 };

      const { contactsApi } = await import('@/lib/api');
      const result = await contactsApi.getBlocked(businessAccountId);

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      // API client already extracts data.data, so result.data is the array directly
      const backendContacts = (Array.isArray(result.data) ? result.data : []) as BackendContact[];
      return {
        data: backendContacts.map((contact: BackendContact) => transformBackendContactToFrontend(contact)),
        count: result.count || 0,
        totalCount: result.totalCount || 0,
      };
    },
    enabled: !!businessAccountId,
  });
}

// Contact Statistics Hook
export function useContactStats(businessAccountId?: string, type?: string) {
  return useQuery({
    queryKey: ['contact-stats', businessAccountId, type],
    queryFn: async () => {
      if (!businessAccountId) {
        return {
          newContactsToday: 0,
          unsubscribed: 0,
          noInteractivity30Days: 0,
          totalContacts: 0
        };
      }

      const { contactsApi } = await import('@/lib/api');
      const result = await contactsApi.getStats(businessAccountId, type);


      if (result.error) {
        throw ApiError.fromResult(result);
      }

      // API client already extracts data.data, so result.data should be the stats object
      const stats = result.data || {
        newContactsToday: 0,
        unsubscribed: 0,
        noInteractivity30Days: 0,
      };

      return stats;
    },
    enabled: !!businessAccountId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}
